import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import cn from 'classnames';

import PopupManager from '@bibliocommons/base-popup-manager';
import { IconButton } from '@bibliocommons/base-buttons';
import { Close } from '@bibliocommons/base-icons';
import ScreenReaderMessage from '@bibliocommons/base-screen-reader-message';

import Heading from 'app/components/shared/Heading';
import './Popover.scss';

export const DelayedHoverTrigger = ({ children, hoverDelayMilliseconds, isExpanded, openContent }) => {
  const [isMouseEntered, setIsMouseEntered] = useState(false);
  const setTimeoutRef = useRef(null);

  useEffect(() => {
    clearTimeout(setTimeoutRef.current);

    if (!isExpanded && isMouseEntered) {
      setTimeoutRef.current = setTimeout(openContent, hoverDelayMilliseconds);
    }

    return () => {
      clearTimeout(setTimeoutRef.current);
    };
  }, [hoverDelayMilliseconds, isMouseEntered, isExpanded, openContent]);

  return (
    <div
      onFocus={openContent}
      onMouseEnter={() => setIsMouseEntered(true)}
      onMouseLeave={() => setIsMouseEntered(false)}
    >
      {children}
    </div>
  );
};

DelayedHoverTrigger.propTypes = {
  children: PropTypes.node,
  hoverDelayMilliseconds: PropTypes.number,
  isExpanded: PropTypes.bool,
  openContent: PropTypes.func.isRequired
};

function renderCloseButton(closeAndFocusContent) {
  return (
    <IconButton
      className="popover__pop-up__content__close-button"
      dataKey="close-button"
      handleClick={closeAndFocusContent}
    >
      <Close />
      <ScreenReaderMessage>
        <FormattedMessage id="button_close" />
      </ScreenReaderMessage>
    </IconButton>
  );
}

export default function Popover({
  className,
  children,
  dataKey,
  hasTransition,
  hoverDelayMilliseconds,
  offset,
  rightAligned,
  triggerOnHover,
  renderContent,
  title
}) {
  function renderTrigger({ openContent, toggleContent }, isExpanded) {
    const triggerProps = triggerOnHover
      ? { onMouseEnter: openContent, onFocus: openContent }
      : { onClick: toggleContent };

    const hasHoverDelay = triggerOnHover && hoverDelayMilliseconds > 0;

    return hasHoverDelay ? (
      <DelayedHoverTrigger
        isExpanded={isExpanded}
        hoverDelayMilliseconds={hoverDelayMilliseconds}
        openContent={openContent}
      >
        {children}
      </DelayedHoverTrigger>
    ) : (
      <div {...triggerProps}>{children}</div>
    );
  }

  function renderPopUp({ setPopperElement, styles, attributes, closeAndFocusContent }) {
    return (
      <div
        className={cn('popover__pop-up', { 'right-aligned': rightAligned })}
        ref={setPopperElement}
        style={styles.popper}
        {...attributes.popper}
      >
        <div className="popover__pop-up__content">
          {!!title && (
            <Heading size="modest" tagName="h4">
              {title}
            </Heading>
          )}
          {renderContent()}
          {!triggerOnHover && renderCloseButton(closeAndFocusContent)}
        </div>
      </div>
    );
  }

  return (
    <PopupManager
      className={cn('cp-popover', className)}
      renderContent={renderPopUp}
      dataKey={dataKey}
      handleMouseLeave={popupActions => triggerOnHover && popupActions.closeContent()}
      hasTransition={hasTransition}
      offset={offset}
      rightAligned={rightAligned}
    >
      {renderTrigger}
    </PopupManager>
  );
}

Popover.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
  dataKey: PropTypes.string.isRequired,
  hasTransition: PropTypes.bool,
  hoverDelayMilliseconds: PropTypes.number,
  offset: PropTypes.arrayOf(PropTypes.number),
  rightAligned: PropTypes.bool,
  triggerOnHover: PropTypes.bool,
  renderContent: PropTypes.func.isRequired,
  title: PropTypes.node
};

Popover.defaultProps = {
  hasTransition: false,
  hoverDelayMilliseconds: 0
};
